import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular/messages';
import { TranslateService } from '@ngx-translate/core';
import { catchError, exhaustMap, map, of, tap } from 'rxjs';
import { finalizeLoader } from '@app/shared/components';
import { authActions } from '../actions/auth.actions';
import { HttpService } from '../services/http.service';

const ERROR_KEYS: Record<number, string> = {
    401: 'auth.pin.error.invalid',
    429: 'auth.pin.error.too-many',
};

@Injectable()
export class AuthEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);

    loginWithPin$ = createEffect(() => {
        return this.actions.pipe(
            ofType(authActions.loginWithPin),
            exhaustMap(({ pin }) => {
                return this.httpService.loginWithPin(pin).pipe(
                    finalizeLoader('auth-login'),
                    map((payload) => authActions.loginWithPinSuccess({ payload })),
                    catchError((response: HttpErrorResponse) => of(authActions.loginWithPinError({ code: response.status }))),
                );
            }),
        );
    });

    loginWithPinSuccess$ = createEffect(
        () => {
            return this.actions.pipe(
                ofType(authActions.loginWithPinSuccess),
                tap(() => this.messageService.add(this.translateService.instant('auth.pin.success'), 'success')),
            );
        },
        { dispatch: false },
    );

    loginWithPinError$ = createEffect(
        () => {
            return this.actions.pipe(
                ofType(authActions.loginWithPinError),
                tap(({ code }) => {
                    const key = ERROR_KEYS[code] ?? 'auth.pin.error.generic';
                    this.messageService.add(this.translateService.instant(key), 'danger');
                }),
            );
        },
        { dispatch: false },
    );
}
